import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents } from '@/features/catalog/catalog';
import { fetchDashboard, type DashboardSummary } from './dashboard';
import { listWorks, PENDIENTE_DE_COBRAR } from '@/features/works/works';
import { SelectorPeriodo } from '@/features/period/SelectorPeriodo';
import { resumirAtrasos, SIN_ATRASO, type Atraso } from './atrasados';
import { MESES } from '@/features/catalog/catalog';

interface Props {
  /**
   * Ir al listado de trabajos del mes que se está mirando.
   *
   * Antes cada tarjeta llevaba su propio filtro —«pendiente de facturar» abría
   * la lista ya recortada—. Trabajos dejó de tener filtros: el mes es su único
   * eje, y llegar a una lista recortada sin poder ver ni sacar el recorte era
   * el peor de los dos mundos. La tarjeta informa; la lista es la lista.
   */
  onVerTrabajos: () => void;
  onIrAConfig: () => void;
  /** El período no viaja como argumento: lo lee cada pantalla del contexto. */
  onVerLiquidacion: () => void;
  /** El mes agrupado por cliente, que es como se factura. */
  onVerFacturar: () => void;
  onVerReportes: () => void;
  /** Vuelve a Inicio: ahora el resumen es una pantalla y no la pestaña. */
  onVolver: () => void;
}

/**
 * Inicio / Dashboard (Spec §32).
 *
 * Dos reglas gobiernan esta pantalla:
 *
 * 1. **Todo KPI es interactivo** (Spec §33, §35): tocarlo abre el listado
 *    filtrado que lo genera. Un número que no se puede abrir obliga a rehacer
 *    el filtro a mano y, peor, no se puede verificar.
 * 2. **Sin precio de novillo se muestran los kg y se dice "pendiente"**, nunca
 *    un cero. Los kilogramos son un dato operativo real; el equivalente en
 *    pesos, sin precio, no existe todavía.
 */
export function DashboardPage({
  onVerTrabajos,
  onIrAConfig,
  onVerLiquidacion,
  onVerFacturar,
  onVerReportes,
  onVolver,
}: Props) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;

  // El período NO se guarda acá: vive en `PeriodProvider` para que volver de
  // Reportes o de Liquidación no lo reinicie.
  const { year, month } = usePeriod();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atraso, setAtraso] = useState<Atraso>(SIN_ATRASO);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      setCargando(true);
      try {
        const d = await fetchDashboard(orgId, year, month);
        if (cancelado) return;
        setData(d);
        setError(null);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month]);

  // Lo que ya se tendría que haber cobrado.
  //
  // No depende del período que se esté mirando —mira todos— así que se pide una
  // sola vez y no cuando cambia el mes. Es, justamente, lo que uno no ve
  // mirando el mes: la plata que quedó atrás.
  //
  // En silencio como los demás: si falla, el panel sigue sirviendo.
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const sinCobrar = await listWorks(orgId, PENDIENTE_DE_COBRAR, 500);
        if (!cancelado) setAtraso(resumirAtrasos(sinCobrar));
      } catch {
        if (!cancelado) setAtraso(SIN_ATRASO);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  if (!activeOrg || !orgId) return null;

  const p = data?.production;
  // Una organización de una sola persona no tiene saldos entre participantes:
  // mostrar "mi resultado" ahí sería repetir la producción con otro nombre.
  const muestraSaldos = data?.org_kind === 'team';

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="dashboard">
      <button
        type="button"
        onClick={onVolver}
        className="min-h-11 self-start text-sm underline opacity-70"
        data-testid="dashboard-volver"
      >
        ‹ Volver
      </button>

      {/* Selector de mes compartido con Liquidación: era el mismo problema en
          dos pantallas y tenía dos soluciones distintas —una parcial y una
          inexistente—. */}
      <SelectorPeriodo testId="dashboard-periodo" />

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="dashboard-error">
          {error}
        </p>
      )}
      {cargando && !data && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

      {p && (
        <>
          {/* --- Producción del mes (tarea 7.1) --- */}
          <section className="grid grid-cols-2 gap-3" data-testid="kpi-production">
            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-kg"
            >
              <span className="text-lg font-semibold">{p.total_kg} kg</span>
              <span className="text-xs text-[var(--tinta-tenue)]">de novillo en el mes</span>
            </button>

            <button
              type="button"
              onClick={() =>
                p.total_cents === null ? data.is_admin && onIrAConfig() : onVerTrabajos()
              }
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-equivalent"
            >
              {p.total_cents === null ? (
                <>
                  <span className="text-sm font-semibold text-[var(--aviso)]">
                    Precio de novillo pendiente
                  </span>
                  {/* Cargar precios es tarea del administrador. A un miembro no
                      se le ofrece un atajo que la base le va a negar. */}
                  <span className="text-xs text-[var(--tinta-suave)]">
                    {data.is_admin ? (
                      <span className="underline">Cargar precio</span>
                    ) : (
                      'Lo carga un administrador'
                    )}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-lg font-semibold">{formatCents(p.total_cents)}</span>
                  <span className="text-xs text-[var(--tinta-tenue)]">total generado</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-works"
            >
              <span className="text-lg font-semibold">{p.works_count}</span>
              <span className="text-xs text-[var(--tinta-tenue)]">
                trabajo{p.works_count === 1 ? '' : 's'}
              </span>
            </button>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-mobility"
            >
              <span className="text-lg font-semibold">
                {p.mobility_cents === null ? 'Pendiente' : formatCents(p.mobility_cents)}
              </span>
              <span className="text-xs text-[var(--tinta-tenue)]">de movilidad</span>
            </button>
          </section>

          {p.pending_count > 0 && (
            <p
              className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
              data-testid="dashboard-pending-note"
            >
              {p.pending_count === 1
                ? 'Hay 1 trabajo sin precio de referencia.'
                : `Hay ${p.pending_count} trabajos sin precio de referencia.`}{' '}
              Los kilogramos están cargados; el equivalente en pesos se calcula cuando se cargue el
              precio.
            </p>
          )}

          {/* --- Lo que ya se tendría que haber cobrado ---------------------
              Va ARRIBA de «Pendientes» y no adentro, porque no es lo mismo.
              «Pendiente de cobrar» es el estado normal de un mes recién
              facturado; esto es plata que se pasó de su fecha.

              Y sólo aparece cuando hay algo. Un renglón que dice «0 atrasados»
              todos los días es ruido, y el día que diga otra cosa ya nadie lo
              está mirando.
             --------------------------------------------------------------- */}
          {atraso.cantidad > 0 && (
            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex flex-col gap-1 rounded-[var(--radius-control)] border border-[var(--aviso)] bg-[var(--aviso-fondo)] p-4 text-left"
              data-testid="kpi-atrasados"
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-[var(--aviso)]">
                  Deberían estar cobrados
                </span>
                <span className="text-base font-semibold text-[var(--aviso)]">
                  {atraso.total_cents === null ? 'Pendiente' : formatCents(atraso.total_cents)}
                </span>
              </span>
              <span className="text-xs text-[var(--tinta-suave)]">
                {atraso.cantidad} trabajo{atraso.cantidad === 1 ? '' : 's'} de {atraso.clientes}{' '}
                cliente{atraso.clientes === 1 ? '' : 's'}
                {atraso.masViejo && (
                  <>
                    {' · '}lo más viejo, {MESES[atraso.masViejo.month - 1]?.toLowerCase()} de{' '}
                    {atraso.masViejo.year}
                  </>
                )}
              </span>
            </button>
          )}

          {/* --- Pendientes (tarea 7.2) --- */}
          <section className="flex flex-col superficie" data-testid="kpi-pending">
            <h2 className="border-b border-[var(--linea)] px-4 py-2 text-xs font-medium uppercase opacity-60">
              Pendientes
            </h2>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-16 items-center justify-between gap-3 border-b border-[var(--linea)] px-4 py-3 text-left"
              data-testid="card-pending-bill"
            >
              <span className="min-w-0">
                <span className="block text-sm">🧾 Pendiente de facturar</span>
                <span className="block text-xs text-[var(--tinta-suave)]">
                  {p.pending_to_bill.count} trabajo{p.pending_to_bill.count === 1 ? '' : 's'}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold">
                {p.pending_to_bill.amount_cents === null
                  ? 'Pendiente'
                  : formatCents(p.pending_to_bill.amount_cents)}
                <span className="ml-2 opacity-40">›</span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-16 items-center justify-between gap-3 px-4 py-3 text-left"
              data-testid="card-pending-collect"
            >
              <span className="min-w-0">
                <span className="block text-sm">💰 Pendiente de cobrar</span>
                <span className="block text-xs text-[var(--tinta-suave)]">
                  {p.pending_to_collect.count} trabajo{p.pending_to_collect.count === 1 ? '' : 's'}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold">
                {p.pending_to_collect.amount_cents === null
                  ? 'Pendiente'
                  : formatCents(p.pending_to_collect.amount_cents)}
                <span className="ml-2 opacity-40">›</span>
              </span>
            </button>
          </section>

          {/* --- Mi resultado (tarea 7.3). Sólo con equipo. --- */}
          {muestraSaldos && (
            <section className="flex flex-col gap-2 superficie p-4" data-testid="my-result">
              <h2 className="text-xs font-medium uppercase opacity-60">Mi resultado</h2>

              {data.my_result === null ? (
                <p
                  className="text-sm text-[var(--tinta-suave)]"
                  data-testid="my-result-unavailable"
                >
                  {p.pending_count > 0
                    ? 'El saldo se calcula cuando todos los trabajos del mes tengan precio. Con un trabajo pendiente, cualquier saldo sería un número incompleto.'
                    : 'No participás económicamente en este período.'}
                </p>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="opacity-70">Ganaste en el mes</span>
                    <span data-testid="my-earned">
                      {formatCents(
                        data.my_result.earned_honoraria_cents +
                          data.my_result.earned_mobility_cents,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span
                      className="opacity-70"
                      title="Total facturado a mi nombre en el período, cobrado o no."
                    >
                      Cobrás o vas a cobrar
                    </span>
                    <span data-testid="my-billed">{formatCents(data.my_result.billed_cents)}</span>
                  </div>
                  {data.my_result.adjustments_cents !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="opacity-70">Ajustes</span>
                      <span>{formatCents(data.my_result.adjustments_cents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-[var(--linea)] pt-2 text-sm font-semibold">
                    <span>Para emparejar</span>
                    <span
                      className={
                        data.my_result.balance_cents < 0
                          ? 'text-[var(--peligro)]'
                          : 'text-[var(--exito)]'
                      }
                      data-testid="my-balance"
                    >
                      {formatCents(data.my_result.balance_cents)}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--tinta-tenue)]">
                    {data.my_result.balance_cents === 0
                      ? 'Estás a cero este mes.'
                      : data.my_result.balance_cents < 0
                        ? 'Cobraste de más: te toca transferir.'
                        : 'Cobraste de menos: te tienen que transferir.'}
                  </p>
                </>
              )}
            </section>
          )}

          {/* --- A dónde se va desde acá ---------------------------------
              Cada botón dice la pregunta que contesta la pantalla a la que
              lleva, y no sólo su nombre. Son tres pantallas que hablan del
              mismo mes, y con los nombres solos —«Facturar», «Liquidación»,
              «Reportes»— no había forma de saber cuál era para qué: se entraba
              a las tres a ver si era ésta.

              El orden es el del mes: facturar es lo de todas las semanas,
              liquidar es una vez al final. */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onVerFacturar}
              className="flex min-h-12 flex-col items-start justify-center rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 py-2 text-left"
              data-testid="ver-facturar"
            >
              <span className="text-sm font-medium">Facturar</span>
              <span className="text-xs text-[var(--tinta-suave)]">
                Qué le cobro a cada cliente, con las cantidades
              </span>
            </button>

            {/* La ven todos: un miembro accede a SU propia línea y a las
                transferencias donde interviene. */}
            <button
              type="button"
              onClick={onVerLiquidacion}
              className="flex min-h-12 flex-col items-start justify-center rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 py-2 text-left"
              data-testid="ver-liquidacion"
            >
              <span className="text-sm font-medium">Liquidación</span>
              <span className="text-xs text-[var(--tinta-suave)]">
                Cuánto le toca a cada uno, y cerrar el mes
              </span>
            </button>

            <button
              type="button"
              onClick={onVerReportes}
              className="flex min-h-12 flex-col items-start justify-center rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 py-2 text-left"
              data-testid="ver-reportes"
            >
              <span className="text-sm font-medium">Reportes</span>
              <span className="text-xs text-[var(--tinta-suave)]">
                Para llevarse el mes en una planilla o en PDF
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
